import { tabbable } from 'tabbable'

import BreakpointTracker from './breakpoint-tracker'

export default {

    mixins: [
        BreakpointTracker,
    ],

    data() {
        return {
            /**
             * @type {HTMLElement|SVGElement|Element|null}
             */
            loopBackElement: null,
            /**
             * @type {HTMLElement|SVGElement|Element|null}
             */
            loopSourceElement: null,
            /**
             * @type {HTMLElement|SVGElement|Element|null}
             */
            tabbableRootNodeRef: null,
            /**
             * @type {Array<HTMLElement|SVGElement|Element>|null}
             */
            tabbables: null,
            tabbingLogType: 'Loopback Tabbing',
        }
    },

    watch: {

        /**
         * Watcher
         * @param {HTMLElement|SVGElement|Element|null} newValue
         * @param {HTMLElement|SVGElement|Element|null} oldValue
         */
        tabbableRootNodeRef(newValue, oldValue) {
            if (newValue !== undefined && newValue !== oldValue)
                this.$logger.logObject(newValue, 'tabbableRootNodeRef has been set', this.tabbingLogType)
        },
    },

    methods: {

        /**
         * Clears the event listeners.
         * @private
         */
        _clearEventListenersInLoop() {
            const elementsWithEventListeners = Array.prototype
                .filter(el => el.removeEventListener)
            this.$logger.log(`${ elementsWithEventListeners.length } items with event listeners to clear`, this.tabbingLogType)
            for (let i = 0; i < elementsWithEventListeners.length; i++)
                elementsWithEventListeners[i].removeEventListener('blur', this.onTabLoopBack)
        },

        /**
         * Loads the ends of the loop into data.
         * @private
         * @returns {boolean} A flag to indicate if both ends were loaded.
         */
        _getLoopEnds() {
            this.loopSourceElement = this.tabbables[this.tabbables.length-1]
            this.loopBackElement = this.tabbables[0]
            this.$logger.logObject({ loopSource: this.loopSourceElement, loopBack: this.loopBackElement }, 'Loopbacks', this.tabbingLogType)

            return this.loopSourceElement !== null && this.loopBackElement !== null
        },

        /**
         * Validates the root node.
         * @private
         * @param {HTMLElement|SVGElement|Element} rootNode The element to look for tabbable elements from.
         */
        _validateRootNode(rootNode = this.tabbableRootNodeRef) {
            if (rootNode === undefined) throw new Error(`'tabbableRootNodeRef' value provided is undefined`)
            if (rootNode === null) throw new TypeError(`'tabbableRootNodeRef' cannot be null when binding`)
            this.$logger.logObject(rootNode, 'Querying root node', this.tabbingLogType)
        },

        /**
         * Binds the tabbing events in a loop within the root component node.
         * @param {HTMLElement|SVGElement|Element} rootNode The element to look for tabbable elements from.
         */
        bindTabbingEvents(rootNode = this.tabbableRootNodeRef) {
            this._validateRootNode(rootNode)
            this.$nextTick(() => {
                const tabbables = tabbable(rootNode)
                this.tabbables = tabbables.filter(t => !t.classList.contains('--js-exclude-tab'))
                this.$logger.logObject(this.tabbables, 'Binding tabbing events', this.tabbingLogType)
                if (this.tabbables.length && this.tabbables.length > 0) {
                    if (!this._getLoopEnds()) return
                    this._clearEventListenersInLoop()

                    this.loopSourceElement.addEventListener('blur', this.onTabLoopBack)
                    this.$logger.log('Tab events bound', this.tabbingLogType)
                }
            })
        },

        /**
         * Clears any tabbing events in a loop within the root component node.
         * @param {HTMLElement|SVGElement|Element} rootNode The element to look for tabbable elements from.
         */
        clearTabbingEvents(rootNode = this.tabbableRootNodeRef) {
            this._validateRootNode(rootNode)
            this.$nextTick(() => {
                this.tabbables = tabbable(rootNode)
                this.$logger.logObject(this.tabbables, 'Clearing tabbing events', this.tabbingLogType)
                if (this.tabbables.length && this.tabbables.length > 0) {
                    if (!this._getLoopEnds()) return
                    this._clearEventListenersInLoop()
                    this.$logger.log('Tab events cleared', this.tabbingLogType)
                }
            })
        },

        /**
         * GIves focus to the provided element if it can take it and warns if not.
         * @param {HTMLElement|SVGElement|Element} element The element to look for tabbable elements from.
         * @param {?Event} triggerEvent An optional event triggering the focus change, if relevant.
         */
        giveFocus(element, triggerEvent = null) {
            if (!element) throw new TypeError(`'element' for focus is undefined or null`)
            if (element && element.focus) {
                element.focus()
                if (triggerEvent) triggerEvent.preventDefault()
            } else {
                this.$logger.warn('Loopback element will not accept focus', this.tabbingLogType)
            }
        },

        /**
         * Gets all tabbable elements within the root component node into the tabbables data item.
         * @param {HTMLElement|SVGElement|Element} rootNode The element to look for tabbable elements from.
         */
        loadTabbables(rootNode = this.tabbableRootNodeRef) {
            this._validateRootNode(rootNode)
            this.$nextTick(() => {
                this.tabbables = tabbable(rootNode)
                this.$logger.logObject(this.tabbables, 'Elements loaded', this.tabbingLogType)
            })
        },

        /**
         * Called when the breakpoint changes.
         * @param {string} activeBreakpoint The name of the active breakpoint (also see this.activeBreakpoint)
         */
        onBreakpointChange(activeBreakpoint) {
            this.$logger.log(`Breakpoint has changed to ${ activeBreakpoint }`, 'Responsive')
            if (this.tabbableRootNodeRef)
                this.bindTabbingEvents()
        },

        /**
         * An event to loop back.
         * @param {Event} $event The event
         */
        onTabLoopBack($event) {
            if (this.loopBackElement !== null)
                this.giveFocus(this.loopBackElement, $event)
        },
    },
}
